import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import CountryModal from './CountryModal';
import { showSuccess, showError } from '../../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Country {
  country_id: number;
  country_name: string;
  country_status: number;
}

const Countries: React.FC = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Country | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const cancel: PopconfirmProps['onCancel'] = () => {
    showError('Cancelled');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchCountries = async () => {
    try {
      const data = await apiRequest<Country[]>({
        method: 'get',
        url: 'country',
      });
      setCountries(data);
    } catch (error) {
      showError('Failed to fetch countries');
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  const handleEditClick = (record: Country) => {
    setIsEditMode(true);
    setInitialValues(record);
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchCountries();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (country_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-country/${country_id}`,
      });
      showSuccess('Country deleted');
      fetchCountries();
    } catch {
      showError('Failed to delete country');
    }
  };

  const handleStatusToggle = async (country_id: number) => {
    setCountries((prev) =>
      prev.map((item) =>
        item.country_id === country_id
          ? { ...item, country_status: item.country_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-country/${country_id}`,
      });
      showSuccess('Country status updated');
    } catch {
      showError('Failed to update status');
      fetchCountries();
    }
  };

  const handleTableChange: TableProps<Country>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const columns: TableColumnsType<Country> = [
    {
      title: 'Country Name',
      dataIndex: 'country_name',
      key: 'country_name',
      filters: Array.from(new Set(countries.map((c) => c.country_name))).map(
        (name) => ({
          text: name,
          value: name,
        })
      ),
      filteredValue: filteredInfo.country_name || null,
      onFilter: (value, record) =>
        record.country_name.includes(value as string),
      sorter: (a, b) => a.country_name.localeCompare(b.country_name),
      sortOrder:
        sortedInfo.columnKey === 'country_name' ? sortedInfo.order : null,
      ellipsis: true,
    },
  ];

  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_, record) => (
        <Space>
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checked={!!record.country_status}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                onChange={() => handleStatusToggle(record.country_id)}
              />
              <Button onClick={() => handleEditClick(record)}>
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this country?"
              onConfirm={() => handleDelete(record.country_id)}
              onCancel={cancel}
            >
              <Button danger>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Countries</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Button type="primary" onClick={showCreateModal}>
            Add Country
          </Button>
        )}
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Country>
          className={styles.customTable}
          columns={columns}
          rowKey="country_id"
          // Slice data based on current page and pageSize
          dataSource={countries.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          pagination={false} // disable AntD built-in pagination
          bordered
          footer={() => (
            <Flex justify="space-between" align="center">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, countries.length)} of {countries.length}
              </span>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={countries.length}
                showSizeChanger
                onChange={(page, pageSize) =>
                  setPagination({ current: page, pageSize: pageSize || pagination.pageSize })
                }
                pageSizeOptions={['5', '10', '20', '50']}
              />
            </Flex>
          )}
        />

      </div>

      <CountryModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default Countries;
